import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quotes, MapPin, CircleNotch, Airplane } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Stars({ rating, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5 text-[#D4AF37]">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} weight={n <= rating ? "fill" : "regular"} />
      ))}
    </div>
  );
}

export default function PublicReviews() {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/reviews`);
        setReviews(data);
      } catch {
        setReviews([]);
      }
    })();
  }, []);

  const avg = reviews && reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="min-h-screen bg-ink text-white font-sans">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Logo size="default" />
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 py-20 text-center">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] mb-4 font-sans">Traveler Testimonials</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight mb-6" data-testid="reviews-title">
          Stories from <span className="italic text-[#D4AF37]">those we've served</span>
        </h1>
        <p className="max-w-2xl mx-auto text-white/60 leading-relaxed">
          Every itinerary is a relationship. Here's what our travelers say after returning home.
        </p>

        {avg && (
          <div className="mt-10 inline-flex items-center gap-4 bg-ink-surface border border-[#D4AF37]/20 rounded-full px-6 py-3" data-testid="reviews-summary">
            <Stars rating={Math.round(parseFloat(avg))} size={18} />
            <span className="text-white font-serif text-xl">{avg}</span>
            <span className="text-white/50 text-xs uppercase tracking-widest">
              {reviews.length} review{reviews.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </section>

      {/* Reviews */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        {reviews === null ? (
          <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>
        ) : reviews.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-lg p-16 text-center text-white/50">
            No reviews yet. Be among the first to share your journey.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="reviews-grid">
            {reviews.map((r) => (
              <article key={r.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl p-7 relative overflow-hidden" data-testid={`review-${r.id}`}>
                <Quotes size={44} weight="duotone" className="absolute -top-2 -right-2 text-[#D4AF37]/10" />
                <div className="flex items-center justify-between mb-4">
                  <Stars rating={r.rating} />
                  {r.destination && (
                    <p className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                      <MapPin size={11} weight="duotone" /> {r.destination}
                    </p>
                  )}
                </div>
                <blockquote className="font-serif text-xl text-white/90 leading-relaxed mb-5 italic">
                  "{r.testimonial}"
                </blockquote>
                <div className="pt-4 border-t border-white/5">
                  <p className="text-sm text-white">{r.client_name || "A valued client"}</p>
                  <p className="text-xs text-white/50 mt-0.5">
                    {r.trip_name}{r.agent_name && <> · with <span className="text-[#D4AF37]">{r.agent_name}</span></>}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-[11px] uppercase tracking-[0.35em] text-white/40 flex items-center justify-center gap-2">
        <Airplane size={14} weight="duotone" className="text-[#D4AF37]" style={{ transform: "rotate(-25deg)" }} />
        Best Time Travel &amp; Tours
      </footer>
    </div>
  );
}
